import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  LoadingOverlay,
  MultiSelect,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconInfoCircle, IconPlus, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  Currency,
  PaymentRequirement,
  PaymentRequirementStatus,
} from '@pta/types';
import { ALL_PAYMENT_REQUIREMENT_STATUSES } from '@pta/types';
import {
  fetchGroupCurrencyConfig,
  fetchGroupRequirements,
  type GroupCurrencyConfig,
} from '../api/payment-requirements';
import { fetchCurrencies } from '../api/currencies';
import { ApiError } from '../api/client';
import { useMoneyFormatter } from '../util/money';
import { RequirementFormModal } from '../components/RequirementFormModal';
import { useAuth } from '../auth/AuthProvider';

/**
 * F2b — Aktív csoport befizetési előírás listája.
 *
 * A csoport azonosítóját az AuthProvider activeGroupId-jából vesszük
 * (header-ben lévő GroupPicker állítja). Ha nincs aktív csoport,
 * üres állapotot mutatunk.
 *
 * Pénzformázás: a csoport `GroupLocale`-jából (useMoneyFormatter).
 */
export function RequirementsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeGroupId, myGroups } = useAuth();
  const { money } = useMoneyFormatter();

  const activeGroup = useMemo(
    () => myGroups.find((g) => g.id === activeGroupId) ?? null,
    [myGroups, activeGroupId],
  );

  const [currencyConfig, setCurrencyConfig] = useState<GroupCurrencyConfig | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const [statusFilter, setStatusFilter] = useState<PaymentRequirementStatus[]>([
    'draft',
    'published',
  ]);
  const [data, setData] = useState<PaymentRequirement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();

  const loadContext = async (groupId: number) => {
    try {
      const [cfg, curs] = await Promise.all([
        fetchGroupCurrencyConfig(groupId),
        fetchCurrencies({ activeOnly: true }),
      ]);
      setCurrencyConfig(cfg);
      setCurrencies(curs);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const loadRequirements = async (groupId: number) => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchGroupRequirements(groupId);
      if (
        statusFilter.length > 0 &&
        statusFilter.length < ALL_PAYMENT_REQUIREMENT_STATUSES.length
      ) {
        setData(rows.filter((r) => statusFilter.includes(r.status)));
      } else {
        setData(rows);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeGroupId !== null) {
      void loadContext(activeGroupId);
    } else {
      setCurrencyConfig(null);
      setData([]);
    }
  }, [activeGroupId]);

  useEffect(() => {
    if (activeGroupId !== null) {
      void loadRequirements(activeGroupId);
    }
  }, [activeGroupId, statusFilter]);

  if (activeGroupId === null) {
    return (
      <Stack gap="md">
        <Title order={2}>
          {t('requirements.page_title', {
            ns: 'fe',
            defaultValue: 'Befizetési előírások',
          })}
        </Title>
        <Alert color="yellow" icon={<IconInfoCircle size={18} />}>
          {t('requirements.no_active_group', {
            ns: 'fe',
            defaultValue:
              'Nincs aktív csoport kiválasztva. A fejléc bal oldalán a csoport-választóból válassz egyet.',
          })}
        </Alert>
      </Stack>
    );
  }

  const statusBadgeColor = (s: PaymentRequirementStatus): string => {
    switch (s) {
      case 'draft':
        return 'gray';
      case 'published':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'closed':
        return 'blue';
    }
  };

  const statusOptions = ALL_PAYMENT_REQUIREMENT_STATUSES.map((s) => ({
    value: s,
    label: t(`requirements.status_${s}`, {
      ns: 'fe',
      defaultValue: s,
    }),
  }));

  return (
    <Stack gap="md" pos="relative">
      <Group justify="space-between">
        <Title order={2}>
          {activeGroup?.name ?? `#${activeGroupId}`} —{' '}
          {t('requirements.page_title', {
            ns: 'fe',
            defaultValue: 'Befizetési előírások',
          })}
        </Title>
        <Group>
          <Tooltip label={t('common.refresh', { ns: 'common', defaultValue: 'Frissítés' })}>
            <ActionIcon onClick={() => void loadRequirements(activeGroupId)} variant="light">
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={openModal}
            disabled={!currencyConfig}
          >
            {t('requirements.new', {
              ns: 'fe',
              defaultValue: 'Új előírás',
            })}
          </Button>
        </Group>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      {currencyConfig && (
        <Card withBorder>
          <Group gap="md">
            <IconInfoCircle size={18} />
            <Text size="sm">
              <strong>
                {t('requirements.currency_primary', {
                  ns: 'fe',
                  defaultValue: 'Elsődleges deviza',
                })}
                :
              </strong>{' '}
              <Badge variant="light">{currencyConfig.primaryCurrency}</Badge>
            </Text>
            {currencyConfig.secondaryCurrency && (
              <Text size="sm">
                <strong>
                  {t('requirements.currency_secondary', {
                    ns: 'fe',
                    defaultValue: 'Másodlagos (tájékoztató)',
                  })}
                  :
                </strong>{' '}
                <Badge variant="light">{currencyConfig.secondaryCurrency}</Badge>
                <small style={{ color: '#888', marginLeft: 6 }}>
                  ({currencyConfig.secondaryCurrencySource})
                </small>
              </Text>
            )}
          </Group>
        </Card>
      )}

      <Card withBorder>
        <MultiSelect
          label={t('requirements.filter_status', {
            ns: 'fe',
            defaultValue: 'Státusz szűrő',
          })}
          data={statusOptions}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as PaymentRequirementStatus[])}
          clearable
        />
      </Card>

      <Card withBorder pos="relative" p={0}>
        <LoadingOverlay visible={loading} />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>
                {t('requirements.col_title', { ns: 'fe', defaultValue: 'Cím' })}
              </Table.Th>
              <Table.Th>
                {t('requirements.col_purpose', { ns: 'fe', defaultValue: 'Cél' })}
              </Table.Th>
              <Table.Th ta="right">
                {t('requirements.col_amount', { ns: 'fe', defaultValue: 'Összeg' })}
              </Table.Th>
              <Table.Th>
                {t('requirements.col_due_date', {
                  ns: 'fe',
                  defaultValue: 'Határidő',
                })}
              </Table.Th>
              <Table.Th>
                {t('requirements.col_status', {
                  ns: 'fe',
                  defaultValue: 'Státusz',
                })}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.length === 0 && !loading && (
              <Table.Tr>
                <Table.Td colSpan={6} align="center">
                  {t('common.no_results', { ns: 'common', defaultValue: 'Nincs találat' })}
                </Table.Td>
              </Table.Tr>
            )}
            {data.map((r) => (
              <Table.Tr
                key={r.id}
                onClick={() => navigate(`/app/requirements/${r.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>{r.id}</Table.Td>
                <Table.Td>{r.title}</Table.Td>
                <Table.Td>
                  <small style={{ color: '#666' }}>{r.purpose}</small>
                </Table.Td>
                <Table.Td ta="right">{money(r.amount)}</Table.Td>
                <Table.Td>{r.dueDate ?? '—'}</Table.Td>
                <Table.Td>
                  <Badge color={statusBadgeColor(r.status)}>
                    {t(`requirements.status_${r.status}`, {
                      ns: 'fe',
                      defaultValue: r.status,
                    })}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      {currencyConfig && (
        <RequirementFormModal
          opened={modalOpened}
          onClose={closeModal}
          groupId={activeGroupId}
          currencyConfig={currencyConfig}
          currencies={currencies}
          onSaved={async (requirementId) => {
            closeModal();
            await loadRequirements(activeGroupId);
            void requirementId;
          }}
        />
      )}
    </Stack>
  );
}
